"use client";
import { useEffect, useState } from "react";
import { Database, Eye, Search, TableProperties } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "./ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "./ui/card";
import { Input } from "./ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import { FaTrash } from "react-icons/fa";
import { FiTrash2 } from "react-icons/fi";

import { ScrollArea } from "./ui/scroll-area";
import styles from "./Styles/SchemaExplorer.module.css";
import { useSelector, useDispatch } from "react-redux";
import { Breadcrumb } from "./ui/breadcrumb";
import { AppSidebar } from "./ui/sidebar";
import { fetchSchema, deleteConnection } from "../../actions/schemaExplorer";
import { getListOfTables } from "../../actions/listOfTables";

import Header from "../header";
import { Fa } from "zod/v4/locales";
import { decryptData } from "../utils/EncriptionStore";
import { custom } from "zod";

export default function SchemaExplorerPage() {
  const dispatch = useDispatch();
  const user = (() => {
    const encryptedData = localStorage.getItem("profile");
    return encryptedData ? decryptData(encryptedData) : null;
  })();

  useEffect(() => {
    dispatch(fetchSchema({}));
  }, []);

  const schemasFromStore = useSelector((state) => state.schemaExplorer.schemas);
  console.log("Schemas from store:", schemasFromStore);

  const [searchTerm, setSearchTerm] = useState("");

  const [selectedSchema, setSelectedSchema] = useState(null);

  useEffect(() => {
    if (schemasFromStore.length > 0) {
      setSelectedSchema((prev) => {
        // Only update if previous schema is invalid
        return !prev || !schemasFromStore.includes(prev)
          ? schemasFromStore[0]
          : prev;
      });
    }
  }, [schemasFromStore]);

  console.log("Selected schema:", selectedSchema);

  useEffect(() => {
    if (selectedSchema) {
      dispatch(getListOfTables({ schema: selectedSchema }));
    }
  }, [selectedSchema, dispatch]);

  const tablesFromStore = useSelector((state) => state.listOfTables.tables);

  console.log("Tables from store:", tablesFromStore);

  const filteredTables =
    Array.isArray(tablesFromStore) &&
    tablesFromStore?.filter((table) =>
      table.toLowerCase().includes(searchTerm.toLowerCase())
    );

  console.log("Filtered tables:", filteredTables);

  const [isOpen, setIsOpen] = useState(true);

  const toggleSidebar = () => setIsOpen((prev) => !prev);

  const handleDeleteSchema = (schemaName) => {
    dispatch(
      deleteConnection({
        name: schemaName,
        customer_id: user?.customer_id,
        group_id: user?.group_id,
        customer_name: user?.customer_name,
        group_name: user?.groupname,
      })
    );
  };

  return (
    <div className={styles.container}>
      <div className={styles.twoSidebars}>
        <Header />
        <div
          className={` ${isOpen ? styles.sidebarOpen : styles.sidebarClosed}`}
        >
          <AppSidebar isOpen={isOpen} toggleSidebar={toggleSidebar} />
        </div>
      </div>
      <div className={`${styles.dashboardLayout} `}>
        {" "}
        <div>
          <h1 className={styles.title}>Schema Explorer</h1>
          <p className={styles.description}>
            Browse and explore database schemas and tables
          </p>
        </div>
        <div className={styles.gridLayout}>
          {/* Schema sidebar */}
          <Card className={styles.card}>
            <CardHeader className={styles.sidebarCard}>
              <CardTitle
                className="text-lg schema-heading"
                style={{ marginLeft: "1.5rem" }}
              >
                Schemas
              </CardTitle>
            </CardHeader>
            <CardContent className={`${styles.schemaCardContent}`}>
              <div className={styles.scrollWrapper}>
                {" "}
                <ScrollArea className="h-[calc(100vh-300px)]">
                  <div className="space-y-1 p-2">
                    {schemasFromStore.map((schema, index) => (
                      <div key={index} className={styles.schemaRow}>
                        <Button
                          className={`${styles.sidebarButton} ${
                            selectedSchema === schema ? styles.selected : ""
                          }`}
                          onClick={() => setSelectedSchema(schema)}
                        >
                          <TableProperties className="h-4 w-4" />
                          <span>{schema}</span>
                        </Button>
                        <FiTrash2
                          className={styles.deleteIcon}
                          onClick={() => handleDeleteSchema(schema)}
                        />
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </div>
            </CardContent>
          </Card>

          {/* Tables view */}
          <Card className={styles.card}>
            <CardHeader className={styles.tablesCard}>
              <div className={styles.tablesHeader}>
                <CardTitle className={styles.tablesTitle}>
                  <Database className="h-5 w-5" />
                  <span>{selectedSchema}</span>
                </CardTitle>
                <div className={styles.searchContainer}>
                  <i>
                    <Search className={styles.searchIcon} />
                  </i>
                  <Input
                    placeholder="Search tables..."
                    className={styles.searchInput}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>
              <CardDescription>
                {filteredTables.length} tables in this schema
              </CardDescription>
            </CardHeader>
            <CardContent className={`${styles.schemaCardContent}`}>
              <div className={styles.scrollWrapper}>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Table Name</TableHead>
                      <TableHead className="w-[100px]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {Array.isArray(filteredTables) &&
                      filteredTables.map((table, index) => (
                        <TableRow key={index}>
                          <TableCell className="font-medium">{table}</TableCell>
                          <TableCell className={styles.actionsButton}>
                            <Button variant="ghost" size="icon" asChild>
                              <Link
                                to={`/inventory_builder?schema=${selectedSchema}&table=${table}`}
                              >
                                <Eye className="h-4 w-4" />
                                <Eye className={styles.eyeIcon} />
                                <span className="sr-only ">View table</span>
                              </Link>
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    {Array.isArray(filteredTables) &&
                      filteredTables.length === 0 && (
                        <TableRow>
                          <TableCell
                            colSpan={4}
                            className={styles.noTablesFound}
                          >
                            No tables found matching "{searchTerm}"
                          </TableCell>
                        </TableRow>
                      )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
